import React from 'react';
import { createPortal } from 'react-dom';
import Card from '../../../../../shared/components/Card';
import { UserWithRole, Referent, ReferentFunction } from '../../../../../shared/services/userService';

interface ReferentsModalProps {
  user: UserWithRole;
  referents: Referent[];
  referentFunctions: ReferentFunction[];
  newReferentFunction: string;
  newReferentDescription: string;
  updating: boolean;
  onFunctionChange: (functionName: string) => void;
  onDescriptionChange: (description: string) => void;
  onAssign: () => void;
  onRemove: (functionName: string) => void;
  onClose: () => void;
}

const ReferentsModal: React.FC<ReferentsModalProps> = ({
  user,
  referents,
  referentFunctions,
  newReferentFunction,
  newReferentDescription,
  updating,
  onFunctionChange,
  onDescriptionChange,
  onAssign,
  onRemove,
  onClose
}) => {
  const getSelectedReferent = (functionId: string) => {
    return referents.find(r => r.function === functionId);
  };

  const getReferentActiveState = (functionId: string) => {
    const selected = referents.find(r => r.function === functionId);
    if (!selected) return 'none';
    return selected.is_active ? 'active' : 'inactive';
  };

  const extractReferentType = (referentFunction: string): string => {
    const functionUpper = referentFunction.toUpperCase();
    if (functionUpper.includes('ADMIN')) return 'Admin';
    if (functionUpper.includes('CM')) return 'CM';
    if (functionUpper.includes('MODO')) return 'Modo';
    if (functionUpper.includes('SCÉNARISTE') || functionUpper.includes('SCENARISTE')) return 'Scénariste';
    if (functionUpper.includes('GR') || functionUpper.includes('GROUPE') || functionUpper.includes('RÉFLEXION') || functionUpper.includes('REFLEXION')) return 'GR';
    return 'Inconnu';
  };

  const getReferentTypeColor = (referentType: string) => {
    const typeUpper = referentType.toUpperCase();
    if (typeUpper.includes('CM')) {
      return 'bg-red-500/20 text-red-400 border-red-500/50';
    }
    if (typeUpper.includes('ADMIN')) {
      return 'bg-orange-500/20 text-orange-400 border-orange-500/50';
    }
    if (typeUpper.includes('MODO')) {
      return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50';
    }
    if (typeUpper.includes('SCÉNARISTE') || typeUpper.includes('SCENARISTE')) {
      return 'bg-blue-500/20 text-blue-400 border-blue-500/50';
    }
    if (typeUpper.includes('GR') || typeUpper.includes('GROUPE') || typeUpper.includes('RÉFLEXION') || typeUpper.includes('REFLEXION')) {
      return 'bg-purple-500/20 text-purple-400 border-purple-500/50';
    }
    return 'bg-gray-500/20 text-gray-400 border-gray-500/50';
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
      <Card className="max-w-2xl w-full p-6 max-h-[80vh] overflow-y-auto">
        <h3 className="text-2xl font-bold text-white mb-4">
          Gérer les référents - {user.username}
        </h3>
        <p className="text-gray-400 mb-6 text-sm">
          Assignez le statut de référent à cet utilisateur. Seuls les membres de l'équipe (CM, Admin, Modo, Scénariste, Groupe de Réflexion) peuvent être référents. Vous pouvez assigner plusieurs fonctions de référent à la même personne, même si elles correspondent à des rôles différents.
        </p>

        {/* Formulaire d'ajout */}
        {referentFunctions.length > 0 && (
          <div className="mb-6 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
            <h4 className="text-white font-semibold mb-3">Assigner ou mettre à jour le référent</h4>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Fonction de référent
                </label>
                <select
                  value={newReferentFunction}
                  onChange={(e) => onFunctionChange(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Sélectionner une fonction</option>
                  {referentFunctions.map((func) => {
                    const state = getReferentActiveState(func.id);
                    const suffix = state === 'active' ? ' (actif)' : state === 'inactive' ? ' (inactif)' : '';
                    return (
                      <option key={func.id} value={func.id}>
                        {func.name} - {func.description}
                        {suffix}
                      </option>
                    );
                  })}
                </select>
                <p className="text-gray-500 text-xs mt-1">
                  Toutes les fonctions de référent disponibles. Vous pouvez en assigner plusieurs à la même personne.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Description (optionnel)
                </label>
                <textarea
                  value={newReferentDescription}
                  onChange={(e) => onDescriptionChange(e.target.value)}
                  placeholder="Description des responsabilités..."
                  rows={2}
                  className="w-full px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <button
                onClick={onAssign}
                disabled={updating || !newReferentFunction}
                className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updating
                  ? 'Mise à jour...'
                  : getSelectedReferent(newReferentFunction)
                    ? 'Mettre à jour le référent'
                    : 'Assigner le statut de référent'}
              </button>
            </div>
          </div>
        )}

        {/* Liste des référents */}
        {referents.length === 0 ? (
          <p className="text-gray-400 text-center py-8">Aucun référent assigné</p>
        ) : (
          <div className="space-y-3">
            <h4 className="text-white font-semibold mb-3">Référents assignés</h4>
            {referents.map((referent) => (
              <div
                key={referent.id}
                className="p-4 bg-gray-800/50 rounded-lg border border-gray-700 flex items-center justify-between"
              >
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="text-white font-semibold">{referent.function}</span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium border ${
                      referent.is_active
                        ? getReferentTypeColor(referent.function)
                        : 'bg-gray-500/20 text-gray-400 border-gray-500/50 opacity-50'
                    }`}>
                      {extractReferentType(referent.function)}
                    </span>
                    {!referent.is_active && (
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-500/20 text-red-400 border border-red-500/50">
                        Inactif
                      </span>
                    )}
                  </div>
                  {referent.description && (
                    <p className="text-gray-400 text-sm mb-1">{referent.description}</p>
                  )}
                  <p className="text-gray-500 text-xs">
                    Assigné le {new Date(referent.assigned_at).toLocaleDateString('fr-FR')}
                    {referent.assigned_by_username && ` par ${referent.assigned_by_username}`}
                  </p>
                </div>
                <div className="ml-4 flex items-center gap-2">
                  <button
                    onClick={() => {
                      onFunctionChange(referent.function);
                      onDescriptionChange(referent.description || '');
                    }}
                    disabled={updating}
                    className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Renseigner la description pour mettre à jour ce référent"
                  >
                    Modifier
                  </button>
                  <button
                    onClick={() => onRemove(referent.function)}
                    disabled={updating}
                    className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Retirer
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <button
          onClick={onClose}
          className="mt-6 w-full px-4 py-3 bg-gray-700 text-white rounded-lg font-semibold hover:bg-gray-600 transition-colors"
        >
          Fermer
        </button>
      </Card>
    </div>,
    document.body
  );
};

export default ReferentsModal;

